import React, { type ReactNode } from "react";
import { AppRoutes } from "@domain/routing/routeConstants";
import {
    ArrowDownToLine,
    ArrowUpFromLine,
    CircleDollar,
    Cube,
    FileCheck,
    FileDollar,
    FileText,
    Firewall,
    House,
    Persons,
    Receipt,
    Sack,
} from "@gravity-ui/icons";
import { GQL } from "@binale-tech/shared";

export type ProductKey = GQL.IProductKey | "accountingCommonOverview";
export class ProductKeys {
    static readonly ER: ProductKey = GQL.IProductKey.Er;
    static readonly ER_A: ProductKey = GQL.IProductKey.ErA; // virtual key, subset of ER: ER->Anzahlungen
    static readonly Deb: ProductKey = GQL.IProductKey.Deb;
    static readonly KB: ProductKey = GQL.IProductKey.Kb;
    static readonly Bank: ProductKey = GQL.IProductKey.Bank;
    static readonly FE: ProductKey = GQL.IProductKey.Fe;
    static readonly POS: ProductKey = GQL.IProductKey.Pos;
    static readonly LA: ProductKey = GQL.IProductKey.La;
    static readonly OCR: ProductKey = GQL.IProductKey.Ocr;
    static readonly Contacts: ProductKey = GQL.IProductKey.Contacts;
    static readonly DMS: ProductKey = GQL.IProductKey.Dms;
    static readonly Banks: ProductKey = GQL.IProductKey.Banks;
    static readonly Invoices: ProductKey = GQL.IProductKey.Invoices;
    static readonly CashRegister: ProductKey = GQL.IProductKey.CashRegister;
    static readonly AVM: ProductKey = GQL.IProductKey.Avm;
    static readonly AccountingCommon: ProductKey = "accountingCommonOverview"; // virtual key for combined records overview like KontenAnsicht

    static all(): GQL.IProductKey[] {
        return [
            GQL.IProductKey.Er,
            GQL.IProductKey.Deb,
            GQL.IProductKey.Kb,
            GQL.IProductKey.Bank,
            GQL.IProductKey.Fe,
            GQL.IProductKey.Pos,
            GQL.IProductKey.La,
            GQL.IProductKey.Avm,
            GQL.IProductKey.Ocr,
            GQL.IProductKey.Contacts,
            GQL.IProductKey.Dms,
            GQL.IProductKey.Banks,
            GQL.IProductKey.CashRegister,
            GQL.IProductKey.Invoices,
        ];
    }
}

const GroupList = ["buch", "avm", "pdf", "contacts", "dms", "banks", "invoices", "cashRegisters"];
export type GroupKey = (typeof GroupList)[number];

export class ProductGroups {
    static readonly Buch: GroupKey = "buch";
    // AVM owns assets, not records: its own group keeps it out of every accounting derivation.
    static readonly AVM: GroupKey = "avm";
    static readonly PDF: GroupKey = "pdf";
    static readonly Contacts: GroupKey = "contacts";

    static readonly DMS: GroupKey = "dms";
    static readonly Banks: GroupKey = "banks";
    static readonly Invoices: GroupKey = "invoices";
    static readonly CashRegisters: GroupKey = "cashRegisters";
}

interface ProductInfo {
    icon: ReactNode;
    link: string;
    group: GroupKey;
    pk?: ProductKey;
}

export class ProductDetails {
    static details: Record<ProductKey, ProductInfo> = {
        [ProductKeys.Deb]: {
            icon: <ArrowUpFromLine className="size-4" />,
            link: AppRoutes.debitor,
            group: ProductGroups.Buch,
        },
        [ProductKeys.ER]: {
            icon: <ArrowDownToLine className="size-4" />,
            link: AppRoutes.invoice,
            group: ProductGroups.Buch,
        },
        [ProductKeys.Bank]: { icon: <House className="size-4" />, link: AppRoutes.bank, group: ProductGroups.Buch },
        [ProductKeys.KB]: { icon: <Sack className="size-4" />, link: AppRoutes.kassenbuch, group: ProductGroups.Buch },
        [ProductKeys.FE]: {
            icon: <Firewall />,
            link: AppRoutes.freieErfassung,
            group: ProductGroups.Buch,
        },
        [ProductKeys.POS]: {
            icon: <CircleDollar className="size-4" />,
            link: AppRoutes.pos,
            group: ProductGroups.Buch,
        },
        [ProductKeys.LA]: {
            icon: <FileDollar className="size-4" />,
            link: AppRoutes.paySlip,
            group: ProductGroups.Buch,
        },

        [ProductKeys.AVM]: {
            icon: <Cube className="size-4" />,
            link: AppRoutes.avm,
            group: ProductGroups.AVM,
        },

        [ProductKeys.OCR]: { icon: <FileText className="size-4" />, link: AppRoutes.ocr, group: ProductGroups.PDF },
        [ProductKeys.Contacts]: {
            icon: <Persons className="size-4" />,
            link: AppRoutes.contacts,
            group: ProductGroups.Contacts,
        },

        [ProductKeys.DMS]: { icon: <FileCheck className="size-4" />, link: AppRoutes.dms, group: ProductGroups.DMS },
        [ProductKeys.Banks]: { icon: <House className="size-4" />, link: AppRoutes.banks, group: ProductGroups.Banks },
        [ProductKeys.Invoices]: {
            icon: <Receipt className="size-4" />,
            link: AppRoutes.invoicing,
            group: ProductGroups.Invoices,
        },
        [ProductKeys.CashRegister]: {
            icon: <Sack className="size-4" />,
            link: AppRoutes.cashRegisters,
            group: ProductGroups.CashRegisters,
        },
    } as Record<ProductKey, ProductInfo>;

    static getDetails(pk: ProductKey): ProductInfo {
        if (!this.details[pk]) {
            throw new Error("invalid product key " + pk);
        }
        const res = this.details[pk];
        res.pk = pk;
        return res;
    }

    static getGroup(gk: GroupKey): ProductInfo[] {
        const productKeys = Object.keys(this.details) as ProductKey[];
        return productKeys.filter((k) => this.details[k].group === gk).map((k) => this.getDetails(k));
    }
}
